/**
 * Transport policy for hub API requests — where a request may travel.
 *
 * `routedFetch` has two transports: the WebRTC data channel to the hub, and
 * plain HTTP. Plain HTTP is only ever correct when the URL actually reaches a
 * HUB. In cloud mode it does not: `config.apiUrl` is the cloud API base
 * (`https://torik.ai/api`, or `/api` when embedded) and a relative path
 * resolves against the page origin (`app.torik.ai`), where nginx routes
 * `location /api/` to the **cloud** app (otto-web) — so a hub path answered
 * there is the cloud app's HTML 404, surfaced in the app as
 * `Received HTML instead of JSON from API (…). Status: 404.` and visible in the
 * cloud host's nginx log as a hub path that "fell off the channel".
 *
 * The rule this module encodes: a request the router sends over the channel is
 * never downgraded to plain HTTP against the cloud origin. When the channel is
 * down it either waits for one that is expected to come up (page boot, hub
 * switch, reconnect) or fails loudly. Requests that point at a hub DIRECTLY —
 * a LAN address, or Electron's `otto-local://` — keep the HTTP fallback: that
 * is the same machine that serves the hub, not the cloud.
 *
 * Everything here is pure (inputs in, plan out) so the decision itself is
 * unit-tested (`hubRequestRouting.test.ts`): the routing bug this replaces was
 * invisible to every build check and only showed up in a production proxy log.
 */
import { isPrivateOrLocalHostname, type RuntimeConfig } from '../config/runtime';

export type TransportMode = RuntimeConfig['mode'];

export interface ChannelState {
  /** `webrtcStore.connected` — the peer connection reports connected. */
  connected: boolean;
  /** `webrtcStore.connecting` — a connect attempt is in flight. */
  connecting: boolean;
  /** `webrtcStore.hubId` — the hub this session targets; survives a drop. */
  hubId: string | null;
}

export interface TransportInput {
  mode: TransportMode;
  channel: ChannelState;
  /** Hub id from the route (`/h/:hubId/…`); null while the route has none. */
  routeHubId: string | null;
  /** Hub id from `otto_active_hub_key` (used before /h/:hubId lands). */
  activeCloudHubId: string | null;
  /** The request URL as the API layer built it — relative or absolute. */
  url: string;
  /** `window.location.origin` — where a relative URL resolves. */
  appOrigin: string;
  /** Configured API bases (`config.apiUrl`, `config.cloudApiUrl`). */
  apiUrls: string[];
  /**
   * `config.hubServed` — this page was served BY a hub on an origin the
   * hostname test cannot see as one (card `t_6d25f68a`). Its same-origin
   * `/api` paths are that hub's own namespace, answered by plain HTTP.
   */
  hubServed?: boolean;
}

export interface TransportPlan {
  /** The request belongs on the WebRTC channel (cloud mode, or this hub's session). */
  viaChannel: boolean;
  /** Plain HTTP would answer this URL at the CLOUD origin, which cannot serve a hub path. */
  cloudBound: boolean;
  /** The channel is up — send the request over it now. */
  useChannelNow: boolean;
  /** Hold the request this long for a channel that is expected to come up. */
  waitForChannelMs: number;
  /** After a channel failure, may the request be retried over plain HTTP? */
  mayFallBackToHttp: boolean;
}

/** How long a hub request waits for an expected channel (boot, hub switch, reconnect). */
export const CHANNEL_WAIT_TIMEOUT_MS = 10_000;

/** Poll interval while waiting for the channel. */
export const CHANNEL_WAIT_POLL_MS = 100;

/**
 * A hub request that the cloud origin cannot answer was not sent at all: the
 * WebRTC channel to the hub was down. Raised instead of letting the request
 * 404 against the cloud app (which reads as "the hub is broken" in the UI).
 */
export class HubChannelUnavailableError extends Error {
  constructor(url: string, waitedMs = 0) {
    const waited = waitedMs > 0 ? ` Waited ${Math.round(waitedMs / 1000)}s for it.` : '';
    super(`Hub API request not sent: the WebRTC channel to the hub is not connected (${url}).${waited}`);
    this.name = 'HubChannelUnavailableError';
  }
}

function originOf(url: string): string | null {
  try {
    const parsed = new URL(url);
    // Non-http(s) schemes (Electron's otto-local://) have no usable origin.
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.origin : null;
  } catch {
    return null;
  }
}

/**
 * True when this URL is answered by a hub without the channel: Electron's
 * `otto-local://` (the main process proxies it) or a LAN/loopback address.
 */
export function isDirectHubUrl(url: string, appOrigin = 'http://localhost'): boolean {
  if (url.startsWith('otto-local://')) return true;
  try {
    return isPrivateOrLocalHostname(new URL(url, appOrigin).hostname);
  } catch {
    return false;
  }
}

/**
 * True when plain HTTP would resolve this request at a WEB origin rather than a
 * hub — one that answers a hub path with a web page instead of JSON.
 *
 * A hub path lives in the `/api/…` namespace, and only a hub serves it. What is
 * left is every web origin:
 *
 * - the app's OWN origin when a web server serves the app. Measured on the
 *   cloud-served build: `GET https://app.torik.ai/api/notifications/preferences`
 *   is answered `200 text/html` by that SPA's own fallback, and the cloud app
 *   answers its HTML 404 for a path it does not have. The API layer reads either
 *   as `Received HTML instead of JSON`, which is how a hub path aimed at a web
 *   server became a sentence about the hub;
 * - a configured cloud API base (`https://torik.ai/api`), which is the same
 *   statement made explicitly.
 *
 * A private or loopback host is a hub (or the dev server proxying to one) and is
 * left alone. So is any URL OUTSIDE `/api/` — media, storage and asset URLs are
 * plain HTTP by design (`https://storage.torik.ai/otto-media/…`), and the earlier
 * rule that only counted configured bases left those correctly alone.
 */
export function resolvesToCloudOrigin(url: string, appOrigin: string, apiUrls: string[] = []): boolean {
  if (url.startsWith('otto-local://')) return false;

  let resolved: URL;
  try {
    resolved = new URL(url, appOrigin);
  } catch {
    return true;
  }

  // A hub is a private address; a public host never is one.
  if (isPrivateOrLocalHostname(resolved.hostname)) return false;

  // A configured cloud base: the explicit form of the rule below, and it covers a
  // base that does not carry `/api` itself.
  if (apiUrls.some((base) => originOf(base) === resolved.origin)) return true;

  // Everything else is cloud-bound only if it is asking for the hub's namespace.
  return /^\/api(?:\/|$)/.test(resolved.pathname);
}

/**
 * True when `url` resolves to the page's OWN origin and carries the hub's
 * `/api` namespace. Paired with `hubServed`, that is a hub path answered by the
 * very hub that served the page — `https://demo.torik.ai/api/devices` is the
 * demo hub's own endpoint, not a cloud one, however public the hostname looks.
 */
export function isOwnOriginApiPath(url: string, appOrigin: string): boolean {
  try {
    const resolved = new URL(url, appOrigin);
    const page = new URL(appOrigin);
    return resolved.origin === page.origin && /^\/api(?:\/|$)/.test(resolved.pathname);
  } catch {
    return false;
  }
}

export function decideTransport(input: TransportInput): TransportPlan {
  const { mode, channel, routeHubId, activeCloudHubId, url, appOrigin, apiUrls, hubServed } = input;

  // A hub-served page's own `/api` namespace is answered by that hub over plain
  // HTTP — the channel is the CLOUD transport and there is none on this origin.
  // Without this branch the router classified `https://demo.torik.ai/api/…` as
  // cloud-bound (public hostname + `/api` path) and refused to send: every API
  // call in the demo house failed with `HubChannelUnavailableError` even though
  // the request's destination was the page's own server.
  if (hubServed === true && isOwnOriginApiPath(url, appOrigin)) {
    return {
      viaChannel: false,
      cloudBound: false,
      useChannelNow: false,
      waitForChannelMs: 0,
      mayFallBackToHttp: true,
    };
  }

  // Route via WebRTC for a genuine cloud connection: the browser cloud app
  // (mode === 'cloud'), or the WebRTC channel belonging to the hub currently
  // shown in the route (its hubId matches) — or to the active hub key while the
  // /h/:hubId route has not landed yet. A connected session alone is NOT enough:
  // a leftover connection to another hub would hijack LOCAL otto-local://
  // requests through the cloud relay.
  const sessionTargetsThisHub =
    !!channel.hubId && (channel.hubId === routeHubId || (!routeHubId && channel.hubId === activeCloudHubId));
  const connectedToThisHub = channel.connected && sessionTargetsThisHub;
  const cloudBound = resolvesToCloudOrigin(url, appOrigin, apiUrls);
  const viaChannel =
    mode === 'cloud' ||
    connectedToThisHub ||
    // The desktop/mobile build with a cloud session whose peer connection is
    // down (reconnect window, page boot): a cloud-bound URL is still a channel
    // request — this is where the desktop app used to 404 against the cloud
    // every few seconds while the status poll kept running.
    (cloudBound && sessionTargetsThisHub);

  if (!viaChannel) {
    // A cloud-bound URL has no plain-HTTP transport at all. Sending it would put a
    // hub path in front of a web server — the cloud app, or this app's own SPA
    // fallback — which answers HTML and reads as a hub that replied strangely.
    // Hold it for a channel that is expected, and refuse it where none is.
    if (cloudBound) {
      const channelExpected = channel.connecting || !!channel.hubId;
      return {
        viaChannel: true,
        cloudBound,
        useChannelNow: false,
        waitForChannelMs: channelExpected ? CHANNEL_WAIT_TIMEOUT_MS : 0,
        mayFallBackToHttp: false,
      };
    }
    return {
      viaChannel: false,
      cloudBound,
      useChannelNow: false,
      waitForChannelMs: 0,
      mayFallBackToHttp: true,
    };
  }

  // A request that would be answered by the cloud origin has no working HTTP
  // transport at all — the channel is the only way it can reach a hub.
  const mayFallBackToHttp = !cloudBound;

  if (channel.connected) {
    return { viaChannel: true, cloudBound, useChannelNow: true, waitForChannelMs: 0, mayFallBackToHttp };
  }

  // Not connected. Wait only when a channel is actually expected (a connect is
  // in flight, or the session's hub is known and reconnecting) — otherwise the
  // request fails immediately instead of hanging on a hub that is not coming.
  const channelExpected = channel.connecting || !!channel.hubId;
  return {
    viaChannel: true,
    cloudBound,
    useChannelNow: false,
    waitForChannelMs: cloudBound && channelExpected ? CHANNEL_WAIT_TIMEOUT_MS : 0,
    mayFallBackToHttp,
  };
}
